import {
    type PriceCalculationUnit,
    type PriceCalculationValueWithUnit,
    type PriceCalculationUnitValueType,
    type BoundDiscreteValue,
    type DiscreteValueOption,
    type ParameterValueKind,
    type ServiceDefinition,
    PriceCalculationParameterInfo,
    type DiscreteValueAppearance,
} from "../api";
import { resolveOptionsValue } from "./resolve-options-value";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a resolved discrete value option.
 */
export type ResolvedDiscreteValueOption = DiscreteValueOption & {
    calculationValue?: PriceCalculationUnitValueType;
};

/**
 * Represents a discrete option in a parameter value selection.
 *
 * This value object holds an option with an ID, value, and label bound to a parameter.
 */
export class ResolvedDiscreteValue extends ResolvedParameterValue<string, BoundDiscreteValue> {
    readonly options: ResolvedDiscreteValueOption[];
    readonly unit?: PriceCalculationUnit;
    readonly appearance: DiscreteValueAppearance;
    /**
     * Creates a new discrete option.
     *
     * @param value The options
     */
    constructor(value: Omit<BoundDiscreteValue, "kind">) {
        super({
            ...value,
            kind: "discrete",
        });
        this.options = value.options?.map(it => resolveOptionsValue(it)) ?? [];
        this.unit = value.unit;
        this.appearance = value.appearance || "native";
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the options.
     *
     * @returns The options
     */
    getOptions(): ResolvedDiscreteValueOption[] {
        return this.options;
    }

    /**
     * Gets the appearance
     *
     * @returns The appearance
     */
    getAppearance(): DiscreteValueAppearance {
        return this.appearance;
    }

    /**
     * Gets the unit for the calculation parameter.
     *
     * @returns The unit for the calculation parameter
     */
    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        if (this.unit === undefined) {
            return [];
        }
        return [
            {
                id: PriceCalculationParameterInfo.Id(this.parameterId.toString()),
                parameterId: this.parameterId,
                unit: this.unit,
                combineWithParameters: this.combineWithParameters,
            },
        ];
    }

    /**
     * Gets the value for the calculation parameter.
     *
     * @param value The value of the parameter
     * @returns The value for the calculation parameter
     */
    getCalculationParameterValues(value: string | undefined): Record<string, PriceCalculationValueWithUnit> {
        if (value === undefined || this.unit === undefined) {
            return {};
        }
        const option = this.options.find(it => it.value === value);
        if (option === undefined || option.calculationValue === undefined) {
            return {};
        }
        return { [this.parameterId.toString()]: { value: option.calculationValue, unit: this.unit } };
    }

    /**
     * Gets the view information for the parameter.
     *
     * @param values The value of the parameter
     * @returns The view information for the parameter
     */
    getViewText(values: string | undefined): string | undefined {
        if (values === undefined) {
            return undefined;
        }
        const option = this.options.find(it => it.value === values);
        if (option === undefined) {
            return undefined;
        }
        return option.label;
    }

    /**
     * Creates a customer-safe version of this discrete value.
     *
     * @param requestPricing - The pricing mode of the service definition
     * @returns For "show-price" and "show-disclosed-price": this instance
     *          For "show-no-price": new instance without calculationValue and combineWithParameters
     */
    toCustomerValue(requestPricing: ServiceDefinition.RequestPricing): ResolvedDiscreteValue {
        if (requestPricing !== "show-no-price") {
            return this;
        }
        return new ResolvedDiscreteValue({
            parameterId: this.parameterId,
            value: this.value,
            options: this.options.map(({ calculationValue: _calculationValue, ...rest }) => rest),
            unit: this.unit,
        });
    }

    /**
     * Creates a new discrete option with valid options.
     *
     * @param value The options bound to a parameter
     * @returns A new discrete option or never if the options are invalid
     */
    static create(value: Omit<BoundDiscreteValue, "kind">): ResolvedDiscreteValue | never {
        const { options } = value;
        const optionValues = options?.map(it => it.value).filter(it => it !== undefined) ?? [];
        if (hasDuplicates(optionValues)) {
            throw new Error(
                "DiscreteOption has values with duplicate a 'value'. Property 'value' must be unique."
            );
        }
        optionValues.forEach(value => {
            if (value === "") {
                throw new Error("DiscreteOption: Property 'value' of value cannot be empty.");
            }
        });

        if (hasDuplicates(options?.map(it => it.label).filter(it => it !== undefined) ?? [])) {
            throw new Error(
                "DiscreteOption has values with duplicate a 'label'. Property 'label' must be unique."
            );
        }

        if (value.value !== undefined && !optionValues.includes(value.value)) {
            throw new Error("DiscreteOption: default value must be one of the defined option values.");
        }

        return new ResolvedDiscreteValue(value);
    }
}

const hasDuplicates = (array: string[]) => new Set(array).size !== array.length;
